/* eslint-disable max-len */
import React from 'react';
import { FormikErrors } from 'formik';
import { AuthMarketing, Button, Checkbox, FieldWrapper, FormError, Input, LayoutSplit, Password } from 'components';
import { appConfig } from 'config/app';
import { AuthColumns, AuthConsentGroup, AuthContainer, AuthContent, AuthContentInner, AuthFieldset } from '../Auth/styles';

interface IFormikEmailSignUpValues {
  firstName: string;
  lastName: string;
  identity: string;
  password: string;
  termsOfService: boolean;
  privacyPolicy: boolean;
}

interface ISignUpEmailProps {
  authHeader: JSX.Element;
  onSubmit: () => void;
  formId: string;
  errors: FormikErrors<IFormikEmailSignUpValues>;
  validateField: (field: 'firstName' | 'lastName' | 'identity' | 'password' | 'termsOfService' | 'privacyPolicy') => boolean;
  isLoading: boolean;
  getFieldProps: (fieldName: string) => any;
  setFieldValue: (fieldName: string, value: any) => void;
  isValid: boolean;
  dirty: boolean;
  values: Record<string, any>;
  errorMessages: string[];
  handleBlur: (e: Event) => void;
}

const formatError = (error: string) => {
  if (error.toLowerCase().trim() === 'this contact is already in use on another account.') {
    return 'An account with this email address already exists.';
  }
  return error;
};

const SignUpEmail = ({ authHeader, onSubmit, errors, validateField, isLoading, getFieldProps, values, errorMessages, isValid, dirty, handleBlur }: ISignUpEmailProps) => (
  <LayoutSplit
    left={
      <AuthContainer>
        <AuthContent data-cy="create-account-email-page-1">
          <AuthContentInner>
            {authHeader}
            <form onSubmit={onSubmit}>
              <AuthFieldset>
                <AuthColumns>
                  <FieldWrapper data-cy="first-name-field" label="First Name" message={validateField('firstName') ? errors.firstName : ''} hasError={validateField('firstName')}>
                    <Input data-cy="first-name-input" disabled={isLoading} id="firstName" type="text" placeholder="John" {...getFieldProps('firstName')} maxLength={30} />
                  </FieldWrapper>
                  <FieldWrapper data-cy="last-name-field" label="Last Name" message={validateField('lastName') ? errors.lastName : ''} hasError={validateField('lastName')}>
                    <Input data-cy="last-name-input" id="lastName" disabled={isLoading} type="text" placeholder="Doe" {...getFieldProps('lastName')} maxLength={30} />
                  </FieldWrapper>
                </AuthColumns>
                <FieldWrapper data-cy="identity-field" label="Email" message={validateField('identity') ? errors.identity : ''} hasError={validateField('identity')}>
                  <Input type="email" disabled={isLoading} placeholder="Email address" onBlur={handleBlur} {...getFieldProps('identity')} />
                </FieldWrapper>

                <FieldWrapper data-cy="password-field" label="Password" message={validateField('password') ? errors.password : ''} hasError={validateField('password')}>
                  <Password data-cy="password-input" disabled={isLoading} id="password" {...getFieldProps('password')} />
                </FieldWrapper>
              </AuthFieldset>

              <AuthConsentGroup>
                <FieldWrapper data-cy="terms-of-service-field" message={validateField('termsOfService') ? errors.termsOfService : ''} indentMessage hasError={validateField('termsOfService')}>
                  <Checkbox
                    data-cy="terms-of-service-input"
                    label="I agree to receive electronic disclosures and understand that Kickfin will send account notices to the email address provided."
                    size="large"
                    id="termsOfService"
                    checked={values.termsOfService}
                    {...getFieldProps('termsOfService')}
                  />
                </FieldWrapper>

                <FieldWrapper data-cy="privacy-policy-field" message={validateField('privacyPolicy') ? errors.privacyPolicy : ''} indentMessage hasError={validateField('privacyPolicy')}>
                  <Checkbox
                    data-cy="privacy-policy-input"
                    label={
                      <span>
                        I have read and agree to{' '}
                        <a target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.TERMS_OF_USE}>
                          Kickfin's User Agreement
                        </a>{' '}
                        and{' '}
                        <a target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.PRIVACY_POLICY}>
                          Privacy Policy.
                        </a>
                      </span>
                    }
                    size="large"
                    id="privacyPolicy"
                    checked={values.privacyPolicy}
                    {...getFieldProps('privacyPolicy')}
                  />
                </FieldWrapper>
              </AuthConsentGroup>

              {errorMessages &&
                errorMessages.map((error: string, index: number) => (
                  <FormError data-cy="form-error" key={`error-message-${index}`}>
                    {formatError(error)}
                  </FormError>
                ))}

              <Button data-cy="submit-button" type="submit" label="Sign Up" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
            </form>
          </AuthContentInner>
        </AuthContent>
      </AuthContainer>
    }
    right={<AuthMarketing />}
  />
);

export default SignUpEmail;
